// Обычно данные необходимые на странице имеет смысл запрашивать прямо на этой странице
// однако в случае с рецептами, в этих данных так же приходит виджет "похожие рецепты"
// которые отрисовывается внутри layout recipes, а поскольку сначала отрисовывается layout
// а только потом page, то этот виджет не приходит со стороны сервера и происходит
// ошибка гидратации, поэтому мы запрашиваем данные со страницы здесь

import { useWidgetsStore } from '~/store/widgets';
import { useServiceErrorsStore } from '~/store/errors';
import { useTalkerStore } from '@devhacker/shared/plugins/talker/store/talker';
import { useArticlesStore } from '~/store/articles';
import { useRecipesListWidgetStore } from '~/store/recipesListWidget';
import { getQueryValue, isRecipePreviewPage } from '~/utils/router';
import { RECIPE_POST_WIDGETS } from '~/constants/widgets';
import { parseData } from '@devhacker/shared/utils/parseData';
import { SingleRecipeSchema } from '~/schemas/wordpress/fetchRecipesSchema';
import { prepareContent } from '~/utils/content/prepareContent';
import { FetchAmpCommentsSchema } from '~/schemas/talker';
import { useRecipeStore } from '~/store/recipe';
import { RecipeSeoInputSchema } from '~/domains/head/seo/api/transformSeo';
import { parseSeoOrThrow } from '~/domains/head/seo/services/parseSeoOrThrow';

export default defineNuxtRouteMiddleware(async (route) => {
  if (import.meta.client) return;
  if (useError().value) return;

  const { $logger } = useNuxtApp();
  const log = $logger.withScope('👨🏻‍🍳 fetch-recipe-data');
  const recipeStore = useRecipeStore();
  const widgetsStore = useWidgetsStore();
  const serviceErrorsStore = useServiceErrorsStore();
  const talkerStore = useTalkerStore();
  const articlesStore = useArticlesStore();
  const recipesListWidgetStore = useRecipesListWidgetStore();
  const { $recipesApi, $talkerApi } = useNuxtApp();
  const isPreview = isRecipePreviewPage(route.name);

  let recipeData;

  if (isPreview) {
    const id = Number(Array.isArray(route.params.id) ? route.params.id[0] : route.params.id);
    log('preview', id);

    if (!id) {
      throw createError({
        statusCode: 404,
        message: 'error in fetch-recipe-data preview "id" is not defined',
      });
    }

    try {
      recipeData = await $recipesApi.fetchRecipePreview(
        id,
        getQueryValue(route.query.preview_type) as
          | 'public-recipe-preview'
          | 'admin-recipe-preview',
        getQueryValue(route.query.preview_type_hash),
      );
    } catch (error) {
      log.error('preview-fetch', error, error?.data, error?.stack);
      throw createError({
        statusCode: error?.data?.data?.status || 503,
        message: `error in fetch-recipe-data ${error?.data?.message}`,
      });
    }
  } else {
    const slug = Array.isArray(route.params.slug) ? route.params.slug[0] : route.params.slug;
    log('slug', slug);

    if (!slug) {
      log.error('error in fetch-recipe-data "slug" is not defined');
      throw new Error('error in fetch-recipe-data "slug" is not defined');
    }

    // widgets for recipe POST
    const widgetsPromise = widgetsStore.fetchRecipeWidgets(RECIPE_POST_WIDGETS);

    try {
      recipeData = await $recipesApi.fetchRecipe(slug);
    } catch (error) {
      log.error('recipe-fetch', error, error?.data, error?.stack);
      throw createError({
        statusCode: error?.data?.data?.status || 503,
        message: `error in fetch-recipe-data ${error?.data?.message}`,
      });
    }

    try {
      const commentsData = await $talkerApi.fetchComments(recipeData?.item?.id, 4, 'oldest');
      const commentsResponse = parseData(commentsData, FetchAmpCommentsSchema);
      serviceErrorsStore.setRecipeCommentsServiceError(false);
      talkerStore.setComments(commentsResponse.data.data.data);
    } catch (error) {
      log.error('comments', error, error?.data, error?.stack);
      serviceErrorsStore.setRecipeCommentsServiceError(true);
    }

    await widgetsPromise;
  }

  if (!recipeData) {
    log.error('no recipe data');
    throw createError({ statusCode: 500, message: 'No data received from useAsyncData' });
  }

  recipeData.item = parseData(recipeData.item, SingleRecipeSchema);
  recipeData.item.parsedContent = prepareContent(recipeData.item.parsedContent, false);
  recipeData.seo = parseSeoOrThrow(RecipeSeoInputSchema, recipeData.seo);

  articlesStore.updateCurrentTitle(recipeData.item.title);

  recipesListWidgetStore.updateCurrentRecipeId(recipeData.item.id);
  recipesListWidgetStore.addRelatedRecipes(
    recipeData.item.relatedPosts,
    recipeData.item.id,
    recipeData.item.relatedPostsMoreLink,
  );
  recipesListWidgetStore.addSimilarRecipes(
    recipeData.item.similarPosts,
    recipeData.item.id,
    recipeData.item.similarPostsMoreLink,
  );

  // не дублировать в store данные
  delete recipeData.item.relatedPosts;
  delete recipeData.item.similarPosts;

  recipeStore.setRecipeData(recipeData);
});
